import React, { useCallback, useEffect, useState } from "react";
import { Box, Flex } from "theme-ui";
import { themevals } from "theme/themevals";
import {
  getDashboardOverview,
  DashboardOverviewData,
  MetricPeriodType,
} from "api/Dashboard";
import PerformanceBlock, { PeriodControl } from "./PerformanceBlock";
import TrendChart from "./TrendChart";
import ArAgingPanel from "./ArAgingPanel";
import ActiveProjectsCard from "./ActiveProjectsCard";
import WorkQueuePanel from "./WorkQueuePanel";
import SupplyBlock from "./SupplyBlock";
import PlanTable from "./PlanTable";
import ScheduleRail from "./ScheduleRail";
import { Skeleton } from "./shared";

/**
 * The redesigned internal dashboard.
 *
 * ONE REQUEST FOR THE WHOLE PAGE (spec 7.15). The old dashboard fires a
 * dozen-plus independent calls; this design adds AR, supply and the plan
 * table on top, so it goes the other way and aggregates.
 *
 * The organising principle, and the reason the layout is what it is:
 * measurement takes the period filter, work does not. The cards, trend
 * context, plan table and supply block answer "how are we doing". The
 * work queue answers "what do we do next" and is deliberately outside
 * the period control.
 *
 * The period lives here rather than in the cards because it drives the
 * plan table too - that is what makes plan numbers reconcile with
 * company numbers.
 */
const DashboardOverview: React.FC = () => {
  const [period, setPeriod] = useState<MetricPeriodType>("month");
  const [range, setRange] = useState<{ from: string; to: string }>({
    from: "",
    to: "",
  });
  const [data, setData] = useState<DashboardOverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (nextPeriod: MetricPeriodType, from: string, to: string) => {
      // A custom period with only one date set is not a request yet. The
      // backend would 400, and showing that error while the operator is
      // still picking the second date is noise, not feedback.
      if (nextPeriod === "custom" && (!from || !to)) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const result = await getDashboardOverview({
          period: nextPeriod,
          ...(nextPeriod === "custom" ? { from, to } : {}),
        });
        setData(result);
      } catch (e: any) {
      // Surfaced rather than swallowed: an empty dashboard reads as a
      // business with no work in it, which is a worse lie than an error.
      //
      // A 404 here means the endpoint is not on this environment's
      // backend yet, which is a deploy-order problem rather than a
      // fault. Saying so saves the next person guessing.
      const status = e?.response?.status;
      setError(
        status === 404
          ? "This environment's backend does not have the dashboard overview endpoint yet. The rest of the page still works."
          : e?.response?.data?.message ||
              "The dashboard figures could not be loaded. Try again shortly.",
      );
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    void load(period, range.from, range.to);
  }, [period, range.from, range.to, load]);

  const headerRow = (
    <Flex
      sx={{
        alignItems: "center",
        justifyContent: "space-between",
        gap: "12px",
        flexWrap: "wrap",
        marginBottom: "16px",
      }}
    >
      <Box
        sx={{
          fontFamily: "Poppins, sans-serif",
          fontSize: "16px",
          fontWeight: 600,
          color: "base_800",
        }}
      >
        Performance
        <Box
          as="span"
          sx={{
            fontSize: "13px",
            fontWeight: 400,
            color: themevals.colors.dkgrey,
            marginLeft: "8px",
          }}
        >
          {data ? data.period.label : ""}
          {data && data.period.partial ? " to date" : ""}
        </Box>
      </Box>
      <PeriodControl
        value={period}
        onChange={setPeriod}
        from={range.from}
        to={range.to}
        onRangeChange={(from, to) => setRange({ from, to })}
        disabled={loading}
      />
    </Flex>
  );

  const awaitingRange = period === "custom" && (!range.from || !range.to);

  if (error) {
    return (
      <Box sx={{ marginBottom: "24px" }}>
        {headerRow}
        <Box
          sx={{
            padding: "16px",
            borderRadius: "6px",
            border: "1px solid",
            borderColor: themevals.colors.inputOutline,
            backgroundColor: "#FDF3F3",
            fontFamily: "Poppins, sans-serif",
            fontSize: "13px",
            color: themevals.colors.danger,
          }}
        >
          {error}
        </Box>
      </Box>
    );
  }

  if (loading || !data) {
    return (
      <Box sx={{ marginBottom: "24px" }}>
        {headerRow}
        <Flex sx={{ gap: "16px", flexWrap: "wrap", marginBottom: "16px" }}>
          {[1, 2, 3, 4].map((i) => (
            <Box key={i} sx={{ flex: "1 1 220px", minWidth: "220px" }}>
              <Skeleton height="140px" />
            </Box>
          ))}
        </Flex>
        <Skeleton height="240px" />
      </Box>
    );
  }

  return (
    <Box sx={{ marginBottom: "24px" }}>
      {/*
        THE PERIOD CONTROL IS INSIDE A BOUNDED BLOCK, AND THAT BOUNDARY IS
        THE POINT. A filter sitting loose at the top of a page implies it
        governs the page - and here it does not: it drives the cards and
        the plan table, and nothing else. Work, AR, the rail and supply
        are live state, deliberately outside it (spec 3.2).

        So everything the period DOES govern is enclosed with it and
        ordered together, and everything it does not is below the divider
        that says so. The plan table moved up here for the same reason -
        it takes the period, so it belongs inside the box.
      */}
      <Box
        sx={{
          backgroundColor: "#FAFBFD",
          border: "1px solid",
          borderColor: themevals.colors.inputOutline,
          borderRadius: "8px",
          padding: "16px",
          marginBottom: "24px",
        }}
      >
        {headerRow}

        {/*
          Custom is selected but only half specified. The figures below are
          still the previous period's, so say so rather than letting them
          read as the range being picked.
        */}
        {awaitingRange && (
          <Box
            sx={{
              marginBottom: "16px",
              padding: "10px 12px",
              borderRadius: "6px",
              backgroundColor: "#FFFFFF",
              border: "1px solid",
              borderColor: themevals.colors.inputOutline,
              fontFamily: "Poppins, sans-serif",
              fontSize: "12px",
              color: themevals.colors.dkgrey,
            }}
          >
            Pick both dates to load a custom range. The figures below are still
            {" " + data.period.label + "."}
          </Box>
        )}

        <PerformanceBlock data={data} />

        <PlanTable plans={data.plans} periodLabel={data.period.label} />
      </Box>

      {/*
        The line between measurement and everything else. Without it the
        panels below look like they simply ignored the filter.
      */}
      <Flex
        sx={{
          alignItems: "center",
          gap: "12px",
          marginBottom: "16px",
        }}
      >
        <Box
          sx={{
            fontFamily: "Poppins, sans-serif",
            fontSize: "13px",
            fontWeight: 600,
            color: "base_800",
            flexShrink: 0,
          }}
        >
          Live now
        </Box>
        <Box
          sx={{
            fontFamily: "Poppins, sans-serif",
            fontSize: "12px",
            color: themevals.colors.dkgrey,
            flexShrink: 0,
          }}
        >
          Current state. Not affected by the period above.
        </Box>
        <Box
          sx={{
            height: "1px",
            flex: 1,
            backgroundColor: themevals.colors.inputOutline,
          }}
        />
      </Flex>

      <Flex sx={{ gap: "16px", flexWrap: "wrap", marginBottom: "24px", alignItems: "stretch" }}>
        {/* Full width above the pair below: it is a single number that
            frames both of them — this is what we are carrying, that is
            what we are owed. */}
        <ActiveProjectsCard activeProjects={data.activeProjects} />
        <TrendChart trend={data.trend} />
        <ArAgingPanel ar={data.ar} unsentDrafts={data.unsentDrafts} />
      </Flex>

      <ScheduleRail schedule={data.schedule} />

      <WorkQueuePanel queue={data.workQueue} />

      <SupplyBlock supply={data.supply} />
    </Box>
  );
};

export default DashboardOverview;
